"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-12 w-3/4 bg-muted rounded-md mx-auto"></div>
        <div className="h-6 w-1/4 bg-muted rounded-md mx-auto"></div>
        <div className="aspect-video w-full bg-muted rounded-md"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-5/6 bg-muted rounded-md"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load the vineyard story.</p>
        <Button onClick={refetch} variant="outline">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <h1 className="text-3xl font-default font-bold text-foreground mb-4">Vintage Not Found</h1>
        <p className="text-muted-foreground mb-8">The story you are looking for has been archived in our cellar.</p>
        <Link to="/ShopPage">
          <Button>Return to Cellar</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-3xl mx-auto">
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-4 flex justify-center gap-2">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="text-sm font-medium text-secondary uppercase tracking-widest">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <h1 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex items-center justify-center gap-4 text-muted-foreground text-sm font-serif italic">
          {post.date && <time dateTime={post.date}>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>}
          {post.date && post.author && <span>•</span>}
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarImage src={post.author.avatar?.['24'] || "/img/sommelier_avatar_small.png"} alt={post.author.name} />
                <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 rounded-md overflow-hidden shadow-lg border border-border">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover aspect-video" 
          />
        </div>
      )}

      <div 
        className="prose prose-lg max-w-none text-foreground font-serif leading-relaxed space-y-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-primary [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>p]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-8"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || '' }} 
      />

      {post.author && (
        <div className="mt-16 p-8 bg-accent/50 rounded-md border border-border flex flex-col sm:flex-row gap-6 items-center sm:items-start">
          <Avatar className="size-24 border-2 border-background shadow-md">
            <AvatarImage src={post.author.avatar?.['96'] || "/img/sommelier_avatar_large.png"} alt={post.author.name} />
            <AvatarFallback className="text-3xl bg-secondary text-secondary-foreground">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left">
            <h4 className="text-xl font-default font-bold text-foreground mb-2">About {post.author.name}</h4>
            <p className="text-muted-foreground font-serif">
              {}
              Master Sommelier at Velvet Vineyards, dedicated to the art of viticulture and the preservation of traditional winemaking techniques in our historic estate.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-80 bg-muted/50 animate-pulse rounded-md"></div>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="h-full flex flex-col overflow-hidden border-border shadow-sm hover:shadow-lg transition-shadow duration-300 bg-card rounded-md">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block aspect-video w-full overflow-hidden">
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" 
              />
            </Link>
          )}
          <CardHeader className="pb-4">
            {post.categories && post.categories.length > 0 && (
              <span className="text-xs font-semibold text-secondary uppercase tracking-widest mb-2 block">
                {post.categories[0].name}
              </span>
            )}
            <CardTitle className="text-xl font-default leading-tight line-clamp-2">
              <Link to={post.link || "#"} className="hover:text-secondary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <p className="text-muted-foreground text-sm line-clamp-3 font-serif">
              {post.excerpt?.replace(/<[^>]*>?/gm, '') || "Discover more about our winemaking process and the heritage of Velvet Vineyards."}
            </p>
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="text-secondary font-medium text-sm hover:underline inline-flex items-center gap-1">
              Read Story
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post' };
  
  const mainPostQuery = new WP_Query(currentWPQueryParams);

  
  const relatedPostsQuery = new WP_Query({ post_type: 'post', per_page: 3 });

  return (
    <section data-section-id="240218" id="single-post-template-section" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <PostsProvider wp_query={mainPostQuery}>
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <div className="mt-24 pt-16 border-t border-border max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h3 className="text-3xl font-default font-bold text-foreground">Vineyard Insights</h3>
            <Link to="/ShopPage">
              <Button variant="outline" className="hidden sm:flex border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground rounded-md">View All Stories</Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-8 text-center sm:hidden">
            <Link to="/ShopPage">
              <Button variant="outline" className="w-full border-secondary text-secondary rounded-md">View All Stories</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}